import { gql, useQuery } from "@apollo/client";
import { useStyletron } from "baseui";
import { Article } from "containers/Articles/articles";
import { ARTICLES_SELECT } from "containers/Articles/articles.gql";
import { useAuth } from "contexts/auth-context";
import React from "react";
import { Controller, UseControllerProps } from "react-hook-form";
import { Ban } from "tabler-icons-react";
import { PERMISSIONS } from "utils/permissions";

import Select, { SelectProps, SelectWithActiveProjectProps } from "./select";

export default function ArticlesSelect({
  disabled,
  activeProjectId,
  ...rest
}: SelectWithActiveProjectProps): React.ReactElement {
  const { checkPermission } = useAuth();
  const [css] = useStyletron();

  const { data, loading } = useQuery(ARTICLES_SELECT, {
    variables: {
      ...(activeProjectId && {
        filter: {
          projectId: {
            eq: activeProjectId,
          },
        },
      }),
    },
  });

  const hasPermission = checkPermission(PERMISSIONS.article.read);

  return (
    <Select
      options={data?.articles?.nodes?.map(({ id, name }: Article) => ({
        id,
        label: name,
      }))}
      getValueLabel={({ option }) => `${option?.label}`}
      getOptionLabel={({ option }) => `${option?.label}`}
      placeholder={
        hasPermission ? (
          "Wybierz"
        ) : (
          <div
            className={css({
              display: "flex",
              alignItems: "center",
              gap: " 5px",
            })}
          >
            <Ban size={16} />
            Brak uprawnień do przeglądania artykułów
          </div>
        )
      }
      disabled={disabled || !hasPermission}
      isLoading={loading}
      {...rest}
    />
  );
}

export function ControlledArticlesSelect({
  control,
  disabled,
  name,
  rules,
  ...rest
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
UseControllerProps<any> & SelectProps): React.ReactElement {
  const { checkPermission } = useAuth();
  const [css] = useStyletron();
  const { data, loading } = useQuery(
    gql`
      query {
        articles(paging: { limit: 10000 }) {
          nodes {
            id
            name
          }
        }
      }
    `
  );

  const hasPermission = checkPermission(PERMISSIONS.article.read);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, onBlur, value, name } }) => (
        <Select
          id={name}
          options={data?.articles?.nodes?.map(({ id, name }: Article) => ({
            id,
            label: name,
          }))}
          placeholder={
            hasPermission ? (
              "Wybierz"
            ) : (
              <div
                className={css({
                  display: "flex",
                  alignItems: "center",
                  gap: " 5px",
                })}
              >
                <Ban size={16} />
                Brak uprawnień do przeglądania artykułów
              </div>
            )
          }
          disabled={disabled || !hasPermission}
          isLoading={loading}
          onChange={(params) => params && onChange(params.value)}
          onBlur={onBlur}
          {...(data && { value: value })}
          {...rest}
        />
      )}
    />
  );
}
